/**
 * SessionDebrief — overlay que se muestra al cerrar el monitor y consume la
 * API /ai/session-debrief con toda la cronología registrada. Presenta:
 *
 *   - Línea de tiempo compacta (qué pasó, cuándo)
 *   - Fortalezas identificadas por la IA
 *   - Omisiones críticas
 *   - Pearls educativas
 *   - Sugerencia de próximo caso
 *
 * Si la IA falla, se muestra al menos la timeline local. Nunca bloquea al
 * alumno porque el debrief es opcional y post-caso.
 */

import { authService } from '../auth/AuthService';
import { sessionRecorder, type SessionRecord } from './SessionRecorder';

interface DebriefResponse {
  summary: string;
  strengths: string[];
  gaps: string[];
  pearls: string[];
  next_case: string | null;
  score_0_100: number;
  /** Si true, el backend no pudo contactar Kimi y devolvió fallback heurístico. */
  used_fallback?: boolean;
}

export class SessionDebrief {
  private root: HTMLElement | null = null;

  async show(): Promise<void> {
    const record = sessionRecorder.end();
    if (!record || record.events.length < 2) {
      console.info('[SessionDebrief] sin eventos suficientes, se omite debrief.');
      return;
    }
    this.render(record, null, true);
    try {
      const res = await authService.request<DebriefResponse>('/ai/session-debrief', {
        method: 'POST',
        body: JSON.stringify({
          case_id: record.caseId,
          diagnosis: record.diagnosis,
          chief_complaint: record.chiefComplaint,
          timeline: sessionRecorder.formatForAI(),
          duration_min: sessionRecorder.summary().durationMin,
        }),
      });
      this.render(record, res, false);
    } catch (e) {
      console.warn('[SessionDebrief] IA sin respuesta:', e);
      this.render(record, null, false);
    }
  }

  private render(record: SessionRecord, res: DebriefResponse | null, loading: boolean): void {
    this.close();
    const root = document.createElement('div');
    root.className = 'cgt-debrief-overlay';
    const summary = sessionRecorder.summary();
    root.innerHTML = `
      <div class="cgt-debrief-card">
        <header>
          <h2>📋 Debrief del caso</h2>
          <button type="button" data-debrief-close aria-label="Cerrar">×</button>
        </header>
        <div class="cgt-debrief-badges">
          <span><strong>${summary.durationMin}</strong> min</span>
          <span><strong>${summary.actions}</strong> acciones</span>
          <span><strong>${summary.drugs}</strong> drogas</span>
          <span><strong>${summary.voice}</strong> turnos voz</span>
          <span><strong>${summary.gestures}</strong> gestos</span>
          ${res ? `<span class="cgt-debrief-score"><strong>${res.score_0_100}</strong>/100</span>` : ''}
        </div>
        ${loading ? `
          <div class="cgt-debrief-loading">
            <div class="cgt-debrief-spinner"></div>
            <span>IA analizando tu desempeño clínico…</span>
          </div>
        ` : ''}
        ${res ? this.renderAIResult(res) : ''}
        ${!loading && !res ? `
          <div class="cgt-debrief-section cgt-debrief-error">
            <p>La IA no pudo generar debrief esta vez. Revisá tu cronología abajo.</p>
          </div>
        ` : ''}
        <details class="cgt-debrief-timeline">
          <summary>Ver cronología completa (${record.events.length} eventos)</summary>
          <ul>
            ${record.events.map(ev => {
              const m = Math.floor(ev.t / 60000);
              const s = Math.floor((ev.t % 60000) / 1000).toString().padStart(2, '0');
              return `<li><code>${m}:${s}</code> · <em>${this.esc(ev.kind)}</em> · ${this.esc(ev.label)}</li>`;
            }).join('')}
          </ul>
        </details>
        <footer>
          <button type="button" data-debrief-export class="cgt-debrief-btn-secondary">⬇ Exportar (.md)</button>
          <button type="button" data-debrief-close class="cgt-debrief-btn-primary">Cerrar</button>
        </footer>
      </div>
    `;
    document.body.appendChild(root);
    this.root = root;
    root.querySelectorAll<HTMLButtonElement>('[data-debrief-close]').forEach(b => {
      b.addEventListener('click', () => this.close());
    });
    root.querySelector<HTMLButtonElement>('[data-debrief-export]')?.addEventListener('click', () => {
      this.exportMarkdown(record, res);
    });
    this.injectStyles();
  }

  /** Genera markdown del debrief y lo descarga como archivo. */
  private exportMarkdown(record: SessionRecord, res: DebriefResponse | null): void {
    const summary = sessionRecorder.summary();
    const date = new Date(record.startedAt).toLocaleString('es-CL');
    const lines: string[] = [];
    lines.push(`# Debrief — ${record.diagnosis ?? 'Caso clínico'}`);
    lines.push('');
    lines.push(`**Fecha:** ${date}  `);
    lines.push(`**Duración:** ${summary.durationMin} min  `);
    lines.push(`**Motivo de consulta:** ${record.chiefComplaint ?? '—'}  `);
    if (res) {
      lines.push(`**Score IA:** ${res.score_0_100}/100  `);
      lines.push('');
      lines.push(`## Resumen`);
      lines.push('');
      lines.push(res.summary);
      if (res.strengths.length) {
        lines.push('');
        lines.push('## ✅ Fortalezas');
        lines.push('');
        res.strengths.forEach(s => lines.push(`- ${s}`));
      }
      if (res.gaps.length) {
        lines.push('');
        lines.push('## ⚠️ Omisiones críticas');
        lines.push('');
        res.gaps.forEach(s => lines.push(`- ${s}`));
      }
      if (res.pearls.length) {
        lines.push('');
        lines.push('## 💡 Pearls educativas');
        lines.push('');
        res.pearls.forEach(s => lines.push(`- ${s}`));
      }
      if (res.next_case) {
        lines.push('');
        lines.push('## 🎯 Próximo caso sugerido');
        lines.push('');
        lines.push(res.next_case);
      }
    }
    lines.push('');
    lines.push(`## Cronología (${record.events.length} eventos)`);
    lines.push('');
    record.events.forEach(ev => {
      const m = Math.floor(ev.t / 60000);
      const s = Math.floor((ev.t % 60000) / 1000).toString().padStart(2, '0');
      lines.push(`- \`${m}:${s}\` *${ev.kind}* — ${ev.label}`);
    });
    lines.push('');
    lines.push(`---`);
    lines.push(`Generado por Dr Newen IA (drnewenia.cloud)`);

    const blob = new Blob([lines.join('\n')], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeDx = (record.diagnosis ?? 'caso').replace(/[^a-z0-9]+/gi, '-').toLowerCase().slice(0, 30);
    const ts = new Date(record.startedAt).toISOString().slice(0, 10);
    a.download = `debrief-${ts}-${safeDx}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  private renderAIResult(res: DebriefResponse): string {
    const fallbackBanner = res.used_fallback ? `
      <div class="cgt-debrief-section" style="background:rgba(255,159,10,0.08); border-left:3px solid #FF9F0A; border-radius:0 8px 8px 0">
        <p style="color:#FF9F0A; font-size:12px; margin:0">
          ⚠️ La IA está caída o sobrecargada. Este análisis es heurístico (no generativo).
          Tu cronología quedó guardada; el profesor la verá con el análisis IA completo cuando el servicio se restablezca.
        </p>
      </div>
    ` : '';
    return `
      ${fallbackBanner}
      <div class="cgt-debrief-section">
        <h3>📝 Resumen</h3>
        <p>${this.esc(res.summary)}</p>
      </div>
      ${res.strengths.length ? `
        <div class="cgt-debrief-section cgt-debrief-strengths">
          <h3>✅ Fortalezas</h3>
          <ul>${res.strengths.map(s => `<li>${this.esc(s)}</li>`).join('')}</ul>
        </div>
      ` : ''}
      ${res.gaps.length ? `
        <div class="cgt-debrief-section cgt-debrief-gaps">
          <h3>⚠️ Omisiones críticas</h3>
          <ul>${res.gaps.map(s => `<li>${this.esc(s)}</li>`).join('')}</ul>
        </div>
      ` : ''}
      ${res.pearls.length ? `
        <div class="cgt-debrief-section cgt-debrief-pearls">
          <h3>💡 Pearls educativas</h3>
          <ul>${res.pearls.map(s => `<li>${this.esc(s)}</li>`).join('')}</ul>
        </div>
      ` : ''}
      ${res.next_case ? `
        <div class="cgt-debrief-section">
          <h3>🎯 Próximo caso sugerido</h3>
          <p>${this.esc(res.next_case)}</p>
        </div>
      ` : ''}
    `;
  }

  close(): void {
    this.root?.remove();
    this.root = null;
  }

  private esc(s: string): string {
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private injectStyles(): void {
    const id = 'cgt-debrief-styles';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
      .cgt-debrief-overlay {
        position: fixed; inset: 0; z-index: 5000;
        background: rgba(0,0,0,0.75); backdrop-filter: blur(10px);
        display: flex; align-items: center; justify-content: center;
        padding: 24px; animation: cgt-dbrf-in 200ms ease-out;
      }
      @keyframes cgt-dbrf-in {
        from { opacity: 0; transform: scale(0.97); }
        to { opacity: 1; transform: scale(1); }
      }
      .cgt-debrief-card {
        background: linear-gradient(180deg, #141414, #1c1c1e);
        border: 0.5px solid rgba(48,209,88,0.3);
        border-radius: 18px;
        width: min(720px, 95vw);
        max-height: 92vh; overflow: auto;
        display: flex; flex-direction: column;
        color: #f5f5f7; font-family: -apple-system, 'Inter', sans-serif;
        box-shadow: 0 30px 80px rgba(0,0,0,0.7);
      }
      .cgt-debrief-card header {
        display: flex; align-items: center; justify-content: space-between;
        padding: 16px 22px; border-bottom: 0.5px solid rgba(255,255,255,0.08);
        position: sticky; top: 0; background: rgba(20,20,20,0.95); backdrop-filter: blur(8px);
        z-index: 1;
      }
      .cgt-debrief-card header h2 { margin: 0; font-size: 18px; font-weight: 700; color: #30D158; }
      .cgt-debrief-card header button {
        background: transparent; border: none; color: #8e8e93;
        font-size: 24px; cursor: pointer; width: 32px; height: 32px; border-radius: 8px;
      }
      .cgt-debrief-card header button:hover { background: rgba(255,69,58,0.15); color: #FF453A; }
      .cgt-debrief-badges {
        display: flex; flex-wrap: wrap; gap: 8px; padding: 14px 22px;
        border-bottom: 0.5px solid rgba(255,255,255,0.05);
      }
      .cgt-debrief-badges span {
        background: rgba(255,255,255,0.05); padding: 4px 10px; border-radius: 20px;
        font-size: 12px; color: #c7c7cc;
      }
      .cgt-debrief-badges strong { color: #fff; }
      .cgt-debrief-score {
        background: rgba(48,209,88,0.15) !important;
        border: 0.5px solid rgba(48,209,88,0.4);
      }
      .cgt-debrief-score strong { color: #30D158 !important; }
      .cgt-debrief-loading {
        display: flex; align-items: center; gap: 12px; padding: 24px 22px;
        color: #8e8e93; font-size: 13px;
      }
      .cgt-debrief-spinner {
        width: 20px; height: 20px; border-radius: 50%;
        border: 2px solid rgba(48,209,88,0.2);
        border-top-color: #30D158;
        animation: cgt-dbrf-spin 0.7s linear infinite;
      }
      @keyframes cgt-dbrf-spin { to { transform: rotate(360deg); } }
      .cgt-debrief-section {
        padding: 14px 22px;
        border-bottom: 0.5px solid rgba(255,255,255,0.05);
      }
      .cgt-debrief-section h3 {
        margin: 0 0 8px 0; font-size: 13px; font-weight: 700;
        color: #f5f5f7; letter-spacing: 0.3px;
      }
      .cgt-debrief-section p {
        margin: 0; font-size: 13px; line-height: 1.55; color: #d1d1d6;
      }
      .cgt-debrief-section ul {
        margin: 0; padding-left: 20px; font-size: 13px; line-height: 1.6; color: #d1d1d6;
      }
      .cgt-debrief-strengths h3 { color: #30D158; }
      .cgt-debrief-gaps h3 { color: #FFD60A; }
      .cgt-debrief-pearls h3 { color: #2997FF; }
      .cgt-debrief-error { color: #FF9F0A; }
      .cgt-debrief-timeline {
        padding: 14px 22px; border-bottom: 0.5px solid rgba(255,255,255,0.05);
      }
      .cgt-debrief-timeline summary {
        cursor: pointer; font-size: 12px; color: #8e8e93; font-weight: 600;
      }
      .cgt-debrief-timeline ul {
        margin: 12px 0 0; padding-left: 14px; font-size: 11px; color: #a1a1a6;
        max-height: 200px; overflow-y: auto;
      }
      .cgt-debrief-timeline code {
        background: rgba(255,255,255,0.06); padding: 1px 4px; border-radius: 4px;
        font-family: 'JetBrains Mono', monospace; font-size: 10px;
      }
      .cgt-debrief-timeline em {
        color: #30D158; font-style: normal; font-weight: 500;
      }
      .cgt-debrief-card footer {
        padding: 14px 22px; display: flex; justify-content: flex-end; gap: 8px;
        position: sticky; bottom: 0; background: rgba(20,20,20,0.95);
        border-top: 0.5px solid rgba(255,255,255,0.05);
      }
      .cgt-debrief-btn-primary {
        background: #30D158; color: #000; border: none;
        padding: 8px 18px; border-radius: 8px; font-weight: 600; cursor: pointer;
        font-size: 13px;
      }
      .cgt-debrief-btn-primary:hover { background: #25A745; }
      .cgt-debrief-btn-secondary {
        background: rgba(255,255,255,0.06); color: #f5f5f7;
        border: 0.5px solid rgba(255,255,255,0.15);
        padding: 8px 14px; border-radius: 8px; font-weight: 500; cursor: pointer;
        font-size: 13px;
      }
      .cgt-debrief-btn-secondary:hover { background: rgba(255,255,255,0.1); }
    `;
    document.head.appendChild(style);
  }
}

export const sessionDebrief = new SessionDebrief();
